// src/pages/memberManagement/components/MemberUpdateHistory.js
import React from "react";
import { Alert, Button, Empty, Spin, Tag, Typography } from "antd";
import { ClockCircleOutlined } from "@ant-design/icons";
import AppBadge from "../../../components/badge/badge";
import "../../../components/customNotification/Notification.css";

const { Text } = Typography;

// Format timestamp
const formatTime = (timestamp) => {
  if (!timestamp) return "Unknown time";

  try {
    const date = new Date(timestamp);
    if (isNaN(date.getTime())) return "Invalid date";

    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));

    if (diffMins < 1) return "Just now";
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays === 1) return "Yesterday";
    if (diffDays < 7) return `${diffDays}d ago`;

    return date.toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit"
    });
  } catch {
    return timestamp;
  }
};

const formatFieldName = (fieldName) => {
  if (!fieldName) return "";
  const fieldMap = {
    address: "Address",
    fullName: "Full Name",
    phoneNumber: "Phone Number",
    email: "Email",
    dateOfBirth: "Date of Birth",
    maritalStatus: "Marital Status",
    baptismStatus: "Baptism Status",
    status: "Membership Status",
    isAdminApproved: "Admin Approval",
    role: "Role",
    roleType: "Role Type",
    dateOfBaptism: "Date of Baptism",
    familyMembers: "Family Members",
    areasOfInterest: "Areas of Interest",
    testimony: "Testimony",
    customInterest: "Custom Interest",
    applicationId: "Application ID",
    userId: "User ID",
    registrationDate: "Registration Date",
    updatedAt: "Updated At",
    children: "Children",
    residenceProofUrl: "Residence Proof",
    baptismCertificateUrl: "Baptism Certificate",
    marriageCertificateUrl: "Marriage Certificate",
    countryCode: "Country Code",
    termsAccepted: "Terms Accepted"
  };

  return fieldMap[fieldName] || fieldName.replace(/([A-Z])/g, " $1").replace(/^./, (str) => str.toUpperCase());
};

// Format value for display
const formatValue = (value) => {
  if (value === null || value === undefined || value === "") {
    return "(empty)";
  }

  // Handle JSON strings
  if (typeof value === "string" && (value.startsWith("{") || value.startsWith("["))) {
    try {
      const parsed = JSON.parse(value);
      if (Array.isArray(parsed)) {
        return parsed.join(", ");
      } else if (typeof parsed === "object") {
        return JSON.stringify(parsed);
      }
    } catch {
      // Not valid JSON, show as is
    }
  }

  // Handle boolean
  if (typeof value === "boolean") {
    return value ? "Yes" : "No";
  }

  return String(value);
};

function MemberUpdateHistory({ updates, loading, error, unreadCount, onMarkAsRead, markAsReadLoadingId }) {
  // Loading state
  if (loading) {
    return (
      <div
        className="member-update-history"
        style={{
          background: "hsl(0, 0%, 100%)",
          border: "1px solid hsl(0, 0%, 91%)",
          borderRadius: 8,
          padding: 40,
          textAlign: "center",
          height: "100%"
        }}
      >
        <Spin tip="Loading activity history..." />
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div
        className="member-update-history"
        style={{
          background: "hsl(0, 0%, 100%)",
          border: "1px solid hsl(0, 0%, 91%)",
          borderRadius: 8,
          padding: 20,
          height: "100%"
        }}
      >
        <Alert message="Error Loading Updates" description={error} type="error" showIcon />
      </div>
    );
  }

  // Empty state
  if (!updates || updates.length === 0) {
    return (
      <div
        className="member-update-history"
        style={{
          background: "hsl(0, 0%, 100%)",
          border: "1px solid hsl(0, 0%, 91%)",
          borderRadius: 8,
          padding: 40,
          textAlign: "center",
          height: "100%"
        }}
      >
        <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="No activity history found" />
      </div>
    );
  }

  return (
    <div
      className="member-update-history"
      style={{
        background: "hsl(0, 0%, 100%)",
        border: "1px solid hsl(0, 0%, 91%)",
        borderRadius: 8,
        padding: 20,
        height: "100%",
        overflowY: "auto",
        maxHeight: "calc(100vh - 200px)"
      }}
    >
      {/* Header */}
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          marginBottom: 20,
          paddingBottom: 12,
          borderBottom: "2px solid hsl(0, 0%, 94%)",
          position: "sticky",
          top: 0,
          background: "hsl(0, 0%, 100%)",
          zIndex: 1
        }}
      >
        <div style={{ display: "flex", alignItems: "center" }}>
          <h4
            style={{
              margin: 0,
              color: "hsl(225, 77%, 53%)",
              fontWeight: 600,
              fontSize: 16
            }}
          >
            Activity History
          </h4>
          {unreadCount > 0 && 
            <AppBadge
              count={unreadCount}
              title={`${unreadCount} unread activity log${unreadCount > 1 ? "s" : ""}`}
            />
          }
        </div>

        <Tag color="blue" style={{ fontWeight: 500 }}>
          {updates.length} {updates.length === 1 ? "activity" : "activities"}
        </Tag>
      </div>

      {/* Activity List - Simple and Clean */}
      <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
        {updates.map((update, idx) => {
          // Parse changes
          let changesObj = update.changes;
          if (typeof changesObj === "string") {
            try {
              changesObj = JSON.parse(changesObj);
            } catch {
              changesObj = {};
            }
          }

          // Get list of changed fields
          const fields = Array.isArray(update.changedFields) && update.changedFields.length > 0 ? update.changedFields : Object.keys(changesObj);

          return (
            <div
              key={update.notificationId || idx}
              style={{
                padding: 16,
                background: update.isUnread ? "hsl(212, 100%, 97%)" : "hsl(0, 0%, 100%)",
                borderRadius: 8,
                border: update.isUnread ? "1px solid hsl(209, 100%, 55%)" : "1px solid hsl(0, 0%, 91%)",
                position: "relative"
              }}
            >
              {/* Unread indicator dot */}
              {update.isUnread && (
                <div
                  style={{
                    position: "absolute",
                    top: -4,
                    right: -4,
                    width: 10,
                    height: 10,
                    borderRadius: "50%",
                    backgroundColor: "hsl(209, 100%, 55%)",
                    border: "2px solid hsl(0, 0%, 100%)"
                  }}
                />
              )}

              {/* Header with user and time */}
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  marginBottom: 12
                }}
              >
                <div>
                  <Text strong style={{ fontSize: 14 }}>
                    {update.userName || "System"}
                  </Text>
                  <Tag color={update.type === "INSERT" ? "green" : update.type === "MODIFY" ? "blue" : "red"} style={{ marginLeft: 8, fontSize: 11 }}>
                    {update.eventName || update.type}
                  </Tag>
                </div>
                <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                  <div style={{ fontSize: 12, color: "hsl(0, 0%, 53%)" }}>
                    <ClockCircleOutlined style={{ marginRight: 4 }} />
                    {update.timeAgo || formatTime(update.timestamp)}
                  </div>
                  {update.isUnread && onMarkAsRead && (
                    <Button
                      size="small"
                      loading={markAsReadLoadingId === (update.eventId || update.notificationId || update.adminNotificationId)}
                      onClick={() => onMarkAsRead(update)}
                      style={{ fontSize: 12 }}
                    >
                      Mark Read
                    </Button>
                  )}
                </div>
              </div>

              {/* List of changes - Simple format */}
              {fields.length > 0 ? (
                <div
                  style={{
                    background: "hsl(0, 0%, 98%)",
                    padding: 12,
                    borderRadius: 6,
                    fontSize: 13
                  }}
                >
                  {fields.map((field, fieldIdx) => {
                    const change = changesObj[field];
                    if (!change) return null;

                    return (
                      <div
                        key={fieldIdx}
                        style={{
                          marginBottom: fieldIdx < fields.length - 1 ? 8 : 0,
                          padding: "4px 0",
                          borderBottom: fieldIdx < fields.length - 1 ? "1px dashed hsl(0, 0%, 91%)" : "none"
                        }}
                      >
                        <Text strong style={{ color: "hsl(225, 77%, 53%)" }}>
                          {formatFieldName(field)}:
                        </Text>{" "}
                        <Text type="secondary">{formatValue(change.oldValue)}</Text> → <Text style={{ color: "hsl(100, 77%, 44%)" }}>{formatValue(change.newValue)}</Text>
                        {change.changeType && change.changeType !== "UPDATED" && (
                          <Tag color={change.changeType === "ADDED" ? "green" : "red"} style={{ marginLeft: 8, fontSize: 10 }}>
                            {change.changeType}
                          </Tag>
                        )}
                      </div>
                    );
                  })}
                </div>
              ) : (
                <div
                  style={{
                    background: "hsl(0, 0%, 98%)",
                    padding: 12,
                    borderRadius: 6,
                    fontSize: 13,
                    color: "hsl(0, 0%, 60%)",
                    fontStyle: "italic"
                  }}
                >
                  No field-level changes tracked
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

export default MemberUpdateHistory;
